import { useMemo } from 'react';
import { useUserContext } from '@/contexts/UserContext';
import { useLocale } from '@/contexts/LocaleContext';
import { BRANDS, DEFAULT_BRAND, type BrandId, type BrandConfig } from '@/config/brand';
import { BRAZIL_LOCATION_ID, isPTLocale } from '@/constants/branding';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';


/**
 * Central branding hook
 * 
 * Brand logic:
 *   - On tributario.com.br → ALWAYS Tributário (domain-locked)
 *   - On tax.network / dev → reactive to locale + location:
 *       Brazil + PT locale → Tributário
 *       Anything else → Tax.Network
 * 
 * DomainEnforcer handles cross-domain redirects when user leaves Brazil+PT scope.
 */
export const useBranding = () => {
  const { currentLocation } = useUserContext();
  const { settings } = useLocale();

  const brandId = useMemo((): BrandId => {
    // On tributario.com.br, brand is ALWAYS tributario (domain-locked)
    if (DOMAIN_DEFAULTS.isTributario) return 'tributario';

    // On tax.network / dev: brand follows user's locale + location
    const isBrazilSelected = currentLocation === BRAZIL_LOCATION_ID;
    const isPT = isPTLocale(settings.locale);
    if (isBrazilSelected && isPT) return 'tributario';

    return DEFAULT_BRAND;
  }, [currentLocation, settings.locale]);

  const brand = BRANDS[brandId];
  const isBrazilBranding = brandId === 'tributario';

  return {
    brandId,
    brand,
    isBrazilBranding,
  };
};

export type { BrandId, BrandConfig };
